class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (overlay) overlay.addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();

    // A page restored by Back (bfcache) or a tab left in the background keeps the
    // drawer and the cart count as they were, while the cart may have changed
    // elsewhere. Re-draw both from the server, without opening the drawer.
    // Back can also re-use the HTTP-cached page (not bfcache): that is a fresh
    // load with a stale cart, so check the navigation type too.
    const navigation = performance.getEntriesByType && performance.getEntriesByType('navigation')[0];
    if (navigation && navigation.type === 'back_forward') this.refresh();
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) this.refresh();
    });
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') this.refresh();
    });
  }

  refresh() {
    if (this.refreshing) return;
    this.refreshing = fetch(`${routes.cart_url}?sections=cart-drawer,cart-icon-bubble`)
      .then((response) => response.json())
      .then((sections) => {
        if (!sections || !sections['cart-drawer']) return;
        const drawer = this.getSectionDOM(sections['cart-drawer'], 'cart-drawer');
        if (drawer) this.classList.toggle('is-empty', drawer.classList.contains('is-empty'));
        this.getSectionsToRender().forEach((section) => {
          const sectionElement = section.selector
            ? document.querySelector(section.selector)
            : document.getElementById(section.id);
          if (!sectionElement || !sections[section.id]) return;
          sectionElement.innerHTML = this.getSectionInnerHTML(sections[section.id], section.selector);
        });
        this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
        if (this.classList.contains('active')) {
          const containerToTrapFocusOn = this.classList.contains('is-empty')
            ? this.querySelector('.drawer__inner-empty')
            : document.getElementById('CartDrawer');
          removeTrapFocus();
          if (containerToTrapFocusOn) trapFocus(containerToTrapFocusOn, this.querySelector('.drawer__inner'));
        }
      })
      .catch(() => {})
      .finally(() => {
        this.refreshing = null;
      });
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  resetInlineTransform() {
    const inner = this.querySelector('.drawer__inner');
    if (!inner) return;
    // Clear all inline transform/transition (including !important set by drag logic)
    inner.style.removeProperty('transform');
    inner.style.removeProperty('transition');
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    this.resetInlineTransform();
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

    // Compensate for scrollbar removal on desktop (prevents layout shift)
    const scrollbarW = window.innerWidth - document.documentElement.clientWidth;
    if (scrollbarW > 0) {
      document.body.style.paddingRight = scrollbarW + 'px';
    }

    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    // IMPORTANT: Remove 'active' FIRST while inline styles (if any) still override CSS.
    // This prevents the one-frame snap to open position that caused close stutter.
    // With inline transform still present (from drag), the drawer stays off-screen.
    // Without inline transform (normal close), CSS transition animates the close.
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
    document.body.style.paddingRight = '';

    // Clear inline transform/transition AFTER class removal.
    // At this point CSS non-active state (105% off-screen) applies.
    // Inline styles are cleared on next frame so there's no visual conflict.
    const inner = this.querySelector('.drawer__inner');
    if (inner) {
      requestAnimationFrame(() => {
        inner.style.removeProperty('transform');
        inner.style.removeProperty('transition');
      });
    }
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      if (this.classList.contains('active')) {
        // Drawer was already open (e.g. a second add-to-cart): open() early-returns
        // and never re-traps focus on the freshly re-rendered DOM, leaving the old
        // trap pointing at destroyed nodes so focus can escape behind the overlay.
        // Re-establish the trap on the new content explicitly.
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        removeTrapFocus();
        if (containerToTrapFocusOn) trapFocus(containerToTrapFocusOn, focusElement);
      } else {
        this.open();
      }
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const el = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return el ? el.innerHTML : '';
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

/* Drag-to-close is handled entirely by af-cart-drawer.js — no duplicate system here. */

